import { ConnectError } from '@bufbuild/connect';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';
import { getName, getImageSrc } from '@/shared/lib';
import { ErrorText, Modal, ModalProps } from '@/shared/ui';

import { DoctorLabel, useSetDoctors } from '@/entities/doctors';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { userModel } from '@/entities/user';

import styles from './RemoveDoctorModal.module.scss';

type RemoveDoctorModalProps = {
  patientID?: string;
  staffID?: string;
} & Pick<ModalProps, 'isOpen' | 'onCancel'>;

export const RemoveDoctorModal: FC<RemoveDoctorModalProps> = (props) => {
  const { isOpen, onCancel: onClose, patientID = '', staffID = '' } = props;

  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { formatMessage } = useIntl();
  const removeDoctor = useSetDoctors();

  const staff = useAppSelector(
    organizationModel.selectors.selectStaffByID(staffID),
  );
  const treatingDoctorsIDs = useAppSelector(
    patientModel.selectors.selectTreatingDoctorsIDs(patientID),
  );
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const treatingDoctorsIDsWithoutRemovedDoctor = (
    treatingDoctorsIDs ?? []
  ).filter((id) => id !== staffID);

  const doctorName = getName(
    staff?.PersonalData?.FirstName,
    staff?.PersonalData?.LastName,
    { userLocale },
  );

  const avatarSrc = getImageSrc(staff?.AvatarAssetID, 'thumbnail');

  const handleClose = () => {
    setErrorMessage(undefined);
    if (onClose) {
      onClose();
    }
  };

  const handleRemoveDoctor = async () => {
    setLoading(true);

    try {
      await removeDoctor({
        patientID,
        staffIDs: treatingDoctorsIDsWithoutRemovedDoctor,
      });
    } catch (error) {
      setErrorMessage((error as ConnectError)?.message);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <Modal
      data-component="remove-doctor-modal"
      containerClassName={styles.container}
      bodyClassName={styles.modalBody}
      title={formatMessage({
        id: 'removeDoctorModal.title',
        defaultMessage: 'Remove treating doctor',
      })}
      isOpen={isOpen}
      onCancel={handleClose}
      onOk={handleRemoveDoctor}
      okButtonText={formatMessage({
        id: 'removeDoctorModal.applyButtonText',
        defaultMessage: 'Remove doctor',
      })}
      okButtonProps={{
        danger: true,
        loading: isLoading,
      }}
    >
      <DoctorLabel fullName={doctorName} avatarSrc={avatarSrc} />
      <ErrorText className={styles.errorText} error={errorMessage} />
    </Modal>
  );
};
